/**
 * The per-agent mini-interview (spec #47 §2) — the ONLY per-agent chat in
 * v1, and deliberately tiny.
 *
 * There is exactly ONE real interview and it is tenant-scoped (P1): its
 * output is the plan, and asking a new agent the same questions again
 * would duplicate it and open a second write path. So this is not a second
 * interview. It is a two-question opening for an agent enabled AFTER
 * provisioning — a new lane from an amendment, or one turned on from the
 * enablement board — over that agent's OWN knobs, rendered by the same
 * `StepRenderer` and written through the same `PUT
 * /api/agents/{domain}/settings`. Never a plan, never a second store.
 *
 * Each question carries the floor it cannot move, in the agent's own
 * words, for the same reason the tenant interview does: the chip narrows
 * autonomy and never widens it, and a screen that implied otherwise would
 * be promising something the engine refuses.
 */

import { useState } from 'react';
import { BlockStack, Button, Card, InlineStack, Text } from '@shopify/polaris';

import type { TreeStep } from '../../../services/onboardingApi';
import { StepRenderer } from '../../onboarding/StepRenderer';
import { updateAgentSettings } from '../../../services/agentSettingsApi';
import type {
  AgentSettings,
  AgentSettingsUpdate,
} from '../../../types/agentSettings';

interface MiniStep {
  step: TreeStep;
  /** The settings write this answer means. One knob, one field. */
  toUpdate: (answer: string) => AgentSettingsUpdate;
}

/** Four chips is the ceiling; two questions is the whole run. */
function steps(agent: AgentSettings): MiniStep[] {
  return [
    {
      step: {
        id: 'autonomy_level',
        prompt: `How should ${agent.display_name} start?`,
        kind: 'chips',
        options: ['manual', 'auto_tier1'],
        option_labels: {
          manual: 'Propose everything',
          auto_tier1: 'Run cheap reversible tests itself',
        },
        why:
          'Spending money or messaging a human will always wait for your ' +
          'approval either way — this only decides the cheap, reversible end.',
        next: 'exploration_fraction',
        next_by_answer: {},
        required: true,
        max_items: null,
      },
      toUpdate: (answer) => ({
        autonomy_level: answer as AgentSettings['autonomy_level'],
      }),
    },
    {
      step: {
        id: 'exploration_fraction',
        prompt: 'How much effort should it spend challenging what it believes?',
        kind: 'chips',
        options: ['0.05', '0.2', '0.5'],
        option_labels: {
          '0.05': 'A little (5%)',
          '0.2': 'Some (20%)',
          '0.5': 'A lot (50%)',
        },
        why:
          'Between 5% and 50% — exploration can be tuned down, never to ' +
          'zero. That floor is what stops it calcifying around one winner.',
        next: null,
        next_by_answer: {},
        required: true,
        max_items: null,
      },
      toUpdate: (answer) => ({ exploration_fraction: Number(answer) }),
    },
  ];
}

export function MiniInterview({
  agent,
  onDone,
}: {
  agent: AgentSettings;
  onDone: () => void;
}) {
  const script = steps(agent);
  const [index, setIndex] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);

  const current = script[index];
  if (!current) return null;

  const answer = async (value: unknown) => {
    setSubmitting(true);
    setRefusal(null);
    try {
      await updateAgentSettings(agent.domain, current.toUpdate(String(value)));
      if (index + 1 >= script.length) {
        onDone();
      } else {
        setIndex(index + 1);
      }
    } catch (e) {
      // Same rule as the tenant interview: the refusal is a turn, and the
      // question stays live with its chips.
      const detail =
        (e as Error & { detail?: string }).detail ||
        (e instanceof Error ? e.message : 'Could not save that.');
      setRefusal(detail);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card>
      <BlockStack gap="300">
        <Text as="h3" variant="headingSm">
          Two quick questions before {agent.display_name} starts
        </Text>
        <StepRenderer
          key={current.step.id}
          step={current.step}
          submitting={submitting}
          refusal={refusal}
          onSubmit={(value) => void answer(value)}
        />
        <InlineStack>
          <Button variant="plain" onClick={onDone}>
            Skip — keep the platform defaults
          </Button>
        </InlineStack>
      </BlockStack>
    </Card>
  );
}

export default MiniInterview;
